import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const TeacherProfile = ({ onNavigate, userInfo }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileData, setProfileData] = useState({});
  const [editedProfile, setEditedProfile] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Sidebar data state (fetched from database like dashboard)
  const [sidebarData, setSidebarData] = useState({
    name: "Loading...",
    teacherId: "...",
    department: "Loading...",
    designation: "Loading...",
    profileImage: null
  });

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load Profile Data from API
  useEffect(() => {
    const fetchTeacherProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔍 Fetching teacher profile data from API...');

        // Get token from storage
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (!token) {
          console.log('❌ No token found, using fallback data');
          setError('Authentication token not found. Please login again.');

          // Set fallback data for sidebar
          if (userInfo) {
            setSidebarData(prev => ({
              ...prev,
              name: userInfo.name || "Teacher User",
              teacherId: userInfo.id || "TCH2024001",
              designation: userInfo.designation || "Lecturer",
              department: userInfo.department || "Computer Science"
            }));
          }
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_LINK}/api/teacher/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('📡 Response status:', response.status);
        console.log('📡 Response ok:', response.ok);

        if (!response.ok) {
          if (response.status === 401) {
            console.log('🔐 Authentication failed');
            setError('Session expired. Please login again.');
            handleLogout();
            return;
          } else if (response.status === 404) {
            console.log('🔍 API endpoint not found');
            setError('API endpoint not found. Please check server configuration.');
            setLoading(false);
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Check if response is JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.log('❌ Response is not JSON:', contentType);
          setError('Server returned non-JSON response. Please check API configuration.');
          setLoading(false);
          return;
        }

        const result = await response.json();
        console.log('📦 API Response:', result);

        if (result.success && result.data) {
          console.log('✅ Teacher profile data received:', result.data);

          const profileInfo = {
            teacherId: result.data.teacherId,
            username: result.data.username,
            name: result.data.name,
            email: result.data.email,
            phone: result.data.phone,
            department: result.data.department,
            designation: result.data.designation,
            profileImage: result.data.profileImage,
            batchName: result.data.batchName,
            bloodGroup: result.data.bloodGroup,
            panNumber: result.data.panNumber,
            adhaarNumber: result.data.adhaarNumber,
            academicQualification: result.data.academicQualification
          };

          setProfileData(profileInfo);
          setEditedProfile(profileInfo);

          // Update sidebar data with fetched information
          setSidebarData({
            name: result.data.name || "Teacher",
            teacherId: result.data.teacherId || userInfo?.id || "TCH2024001",
            department: result.data.department || "Computer Science",
            designation: result.data.designation || "Lecturer",
            profileImage: result.data.profileImage || null
          });

          console.log('✅ Teacher profile and sidebar data updated successfully');

        } else {
          console.log('❌ API returned error:', result.message);
          setError(result.message || 'Failed to fetch profile data');

          // Fallback to userInfo if available
          if (userInfo) {
            console.log('🔄 Using fallback userInfo data');
            setSidebarData(prev => ({
              ...prev,
              name: userInfo.name || prev.name,
              teacherId: userInfo.id || prev.teacherId,
              designation: userInfo.designation || prev.designation,
              department: userInfo.department || prev.department
            }));
          }
        }

      } catch (error) {
        console.error('💥 Error fetching teacher profile:', error);

        // Handle specific errors
        if (error.name === 'SyntaxError' && error.message.includes('Unexpected token')) {
          setError('Server returned HTML instead of JSON. Please check if the API endpoint exists.');
        } else if (error.message.includes('fetch')) {
          setError('Cannot connect to server. Please ensure the server is running');
        } else {
          setError(`Failed to load profile data: ${error.message}`);
        }

        // Fallback to userInfo if available
        if (userInfo) {
          console.log('🔄 Using fallback userInfo data');
          setSidebarData(prev => ({
            ...prev,
            name: userInfo.name || prev.name,
            teacherId: userInfo.id || prev.teacherId,
            designation: userInfo.designation || prev.designation,
            department: userInfo.department || prev.department
          }));
        }
      } finally {
        setLoading(false);
      }
    };

    // Add a small delay to ensure component is mounted
    const timer = setTimeout(fetchTeacherProfile, 500);
    return () => clearTimeout(timer);
  }, [userInfo]);

  // Handle logout
  const handleLogout = () => {
    console.log('🚪 Logging out...');

    // Clear tokens
    sessionStorage.clear();
    localStorage.clear();

    // Make logout API call
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      fetch(`${API_LINK}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }).catch(error => console.error('Logout API error:', error));
    }

    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };

  // Handle profile edit
  const handleEdit = () => {
    setIsEditing(true);
    setSaveMessage(null);
    setSaveError(null);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveError(null);
      setSaveMessage(null);

      // Validate required fields
      if (!editedProfile.name || editedProfile.name.trim() === '') {
        setSaveError('Name is required');
        return;
      }

      // Validate email format if provided
      if (editedProfile.email && editedProfile.email.trim() !== '') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(editedProfile.email)) {
          setSaveError('Please enter a valid email address');
          return;
        }
      }

      // Validate phone number if provided
      if (editedProfile.phone && editedProfile.phone.trim() !== '') {
        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(editedProfile.phone.replace(/\D/g, ''))) {
          setSaveError('Phone number must be exactly 10 digits');
          return;
        }
      }

      console.log('🔄 Saving teacher profile...');

      // Get token
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!token) {
        setSaveError('Authentication token not found. Please login again.');
        return;
      }

      // Prepare form data for multipart upload
      const formData = new FormData();

      // Add profile data as JSON string (ONLY personal information - NO professional fields)
      const profileDataToSend = {
        name: editedProfile.name.trim(),
        email: editedProfile.email ? editedProfile.email.trim() : '',
        phone: editedProfile.phone ? editedProfile.phone.replace(/\D/g, '') : '',
        bloodGroup: editedProfile.bloodGroup ? editedProfile.bloodGroup.trim() : '',
        panNumber: editedProfile.panNumber ? editedProfile.panNumber.trim() : '',
        adhaarNumber: editedProfile.adhaarNumber ? editedProfile.adhaarNumber.toString() : ''
      };

      formData.append('profileData', JSON.stringify(profileDataToSend));

      // Add image file if selected
      if (imageFile) {
        formData.append('profileImage', imageFile);
        console.log('📷 Adding image to form data:', imageFile.name);
      }

      const response = await fetch(`${API_LINK}/api/teacher/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type for FormData, let browser set it with boundary
        },
        body: formData
      });

      if (!response.ok) {
        if (response.status === 401) {
          setSaveError('Session expired. Please login again.');
          handleLogout();
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        console.log('✅ Profile saved successfully:', result.data);

        // Update profile data with response (complete data including professional info)
        const updatedProfile = {
          teacherId: result.data.teacherId,
          username: result.data.username,
          name: result.data.name,
          email: result.data.email,
          phone: result.data.phone,
          profileImage: result.data.profileImage,
          bloodGroup: result.data.bloodGroup,
          panNumber: result.data.panNumber,
          adhaarNumber: result.data.adhaarNumber,
          // Professional fields (returned but not updated)
          department: result.data.department,
          designation: result.data.designation,
          academicQualification: result.data.academicQualification,
          batchName: result.data.batchName
        };

        setProfileData(updatedProfile);
        setEditedProfile(updatedProfile);

        // Update sidebar data as well
        setSidebarData({
          name: result.data.name,
          teacherId: result.data.teacherId,
          department: result.data.department,
          designation: result.data.designation,
          profileImage: result.data.profileImage
        });

        setIsEditing(false);
        setImageFile(null);
        setImagePreview(null);
        setSaveMessage('Profile updated successfully!');

        // Clear success message after 5 seconds
        setTimeout(() => setSaveMessage(null), 5000);

      } else {
        setSaveError(result.message || 'Failed to save profile');
      }

    } catch (error) {
      console.error('💥 Error saving teacher profile:', error);
      setSaveError('Failed to save profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedProfile(profileData);
    setImageFile(null);
    setImagePreview(null);
    setSaveMessage(null);
    setSaveError(null);
  };

  const handleInputChange = (field, value) => {
    // Security: ONLY personal information can be edited
    const allowedFields = ['name', 'email', 'phone', 'bloodGroup', 'panNumber', 'adhaarNumber'];
    if (!allowedFields.includes(field)) {
      console.warn(`🔒 Attempt to edit protected professional field: ${field}`);
      return;
    }

    setEditedProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle image upload
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setSaveError('Image file size must be less than 5MB');
        event.target.value = '';
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setSaveError('Please select a valid image file');
        event.target.value = '';
        return;
      }

      setImageFile(file);
      setSaveError(null);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      console.log('📷 Image selected:', file.name, 'Size:', (file.size / 1024 / 1024).toFixed(2) + 'MB');
    }
  };

  const handleImageRemove = () => {
    setImageFile(null);
    setImagePreview(null);
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Delete profile image via API
  const handleDeleteProfileImage = async () => {
    if (!profileData.profileImage && !imagePreview) {
      return;
    }

    try {
      console.log('🗑️ Deleting profile image...');

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!token) {
        setSaveError('Authentication token not found');
        return;
      }

      const response = await fetch(`${API_LINK}/api/teacher/profile/image`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        const updatedProfile = { ...profileData, profileImage: null };
        setProfileData(updatedProfile);
        setEditedProfile(updatedProfile);
        setSidebarData(prev => ({ ...prev, profileImage: null }));
        handleImageRemove();
        setSaveMessage('Profile image deleted successfully');
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        setSaveError(result.message || 'Failed to delete profile image');
      }

    } catch (error) {
      console.error('Error deleting profile image:', error);
      setSaveError('Failed to delete profile image');
    }
  };

  // Get user initials
  const getUserInitials = (name) => {
    if (!name) return 'TC';
    const names = name.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get profile image URL
  const getProfileImageUrl = () => {
    if (imagePreview) return imagePreview;
    if (sidebarData.profileImage) return `${API_LINK}${sidebarData.profileImage}`;
    if (profileData.profileImage) return `${API_LINK}${profileData.profileImage}`;
    return null;
  };

  // Field icons mapping
  const fieldIcons = {
    name: '👤', email: '✉️', phone: '📱', bloodGroup: '🩸',
    panNumber: '💳', adhaarNumber: '🆔', teacherId: '🏷️',
    username: '🔑', department: '🏛️', designation: '💼',
    academicQualification: '🎓', batchName: '📋'
  };

  const ProfileField = ({ label, value, field, type = "text", readonly = false }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.6rem',
        padding: isMobile ? '0.5rem 0.75rem' : '0.6rem 1rem',
        borderBottom: '1px solid #f0f2f5',
        transition: 'background-color 0.2s ease',
        backgroundColor: 'transparent',
        cursor: 'default'
      }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fafbfc'; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      {/* Icon */}
      <span style={{
        fontSize: isMobile ? '0.85rem' : '0.95rem',
        width: isMobile ? '24px' : '28px',
        height: isMobile ? '24px' : '28px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '6px',
        background: readonly ? '#f0f0f0' : '#e8f5e9',
        flexShrink: 0
      }}>
        {fieldIcons[field] || '📌'}
      </span>

      {/* Label */}
      <label style={{
        fontWeight: '600',
        color: '#444',
        minWidth: isMobile ? '85px' : '145px',
        fontSize: isMobile ? '0.72rem' : '0.8rem',
        letterSpacing: '0.01em',
        flexShrink: 0
      }}>
        {label}
        {readonly && (
          <span style={{
            fontSize: '0.55rem',
            backgroundColor: '#eef1f5',
            color: '#8a8fa3',
            padding: '0.08rem 0.3rem',
            borderRadius: '3px',
            fontWeight: 'normal',
            marginLeft: '0.35rem',
            verticalAlign: 'middle'
          }}>
            🔒
          </span>
        )}
      </label>

      {/* Value / Input */}
      {isEditing && !readonly ? (
        type === 'textarea' ? (
          <textarea
            value={editedProfile[field] || ''}
            onChange={(e) => handleInputChange(field, e.target.value)}
            rows={2}
            style={{
              flex: 1,
              padding: '0.4rem 0.55rem',
              border: '1.5px solid #b8dfc5',
              borderRadius: '6px',
              fontSize: '0.8rem',
              transition: 'all 0.2s ease',
              backgroundColor: '#fff',
              fontFamily: 'inherit',
              resize: 'vertical',
              outline: 'none'
            }}
            onFocus={(e) => { e.target.style.borderColor = '#238548'; e.target.style.boxShadow = '0 0 0 2px rgba(35,133,72,0.1)'; }}
            onBlur={(e) => { e.target.style.borderColor = '#b8dfc5'; e.target.style.boxShadow = 'none'; }}
          />
        ) : (
          <input
            type={type}
            value={editedProfile[field] || ''}
            onChange={(e) => handleInputChange(field, e.target.value)}
            style={{
              flex: 1,
              padding: '0.35rem 0.55rem',
              border: '1.5px solid #b8dfc5',
              borderRadius: '6px',
              fontSize: '0.8rem',
              transition: 'all 0.2s ease',
              backgroundColor: '#fff',
              outline: 'none'
            }}
            onFocus={(e) => { e.target.style.borderColor = '#238548'; e.target.style.boxShadow = '0 0 0 2px rgba(35,133,72,0.1)'; }}
            onBlur={(e) => { e.target.style.borderColor = '#b8dfc5'; e.target.style.boxShadow = 'none'; }}
          />
        )
      ) : (
        <span style={{
          flex: 1,
          color: readonly ? '#7a7f8e' : '#333',
          fontSize: isMobile ? '0.75rem' : '0.82rem',
          fontStyle: !value ? 'italic' : 'normal',
          opacity: !value ? 0.5 : 1
        }}>
          {value || 'Not provided'}
        </span>
      )}
    </div>
  );

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '0.75rem'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #e3f2fd',
          borderTop: '3px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '0.9rem' }}>Loading Profile...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Error Component
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '0.75rem',
        padding: '1.5rem'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
          textAlign: 'center',
          maxWidth: '360px',
          border: '1px solid #e8e8e8'
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>⚠️</div>
          <h3 style={{ color: '#333', margin: '0 0 0.5rem 0', fontSize: '1.1rem', fontWeight: 600 }}>
            Unable to Load Profile
          </h3>
          <p style={{ color: '#666', margin: '0 0 1.25rem 0', fontSize: '0.85rem', lineHeight: 1.5 }}>
            For any further information, please contact your support team.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#238548', color: 'white', border: 'none',
                padding: '0.55rem 1.2rem', borderRadius: '8px', cursor: 'pointer',
                fontSize: '0.85rem', fontWeight: 500, transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#1e7139'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#238548'; }}
            >
              Try Again
            </button>
            <button
              onClick={handleLogout}
              style={{
                backgroundColor: '#666', color: 'white', border: 'none',
                padding: '0.55rem 1.2rem', borderRadius: '8px', cursor: 'pointer',
                fontSize: '0.85rem', fontWeight: 500, transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#555'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#666'; }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout role="teacher" onNavigate={onNavigate} activePage="teacher-profile" userInfo={userInfo}>
      {/* Profile Header Card */}
      <div style={{
        background: '#ffffff',
        borderRadius: '10px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
        marginBottom: isMobile ? '0.75rem' : '1.25rem',
        overflow: 'hidden'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #238548, #1a6b38)',
          color: '#ffffff',
          padding: isMobile ? '1rem 0.75rem' : '1.25rem 1.5rem',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: isMobile ? 'center' : 'space-between',
          alignItems: 'center',
          gap: isMobile ? '0.6rem' : '1rem'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            gap: isMobile ? '0.5rem' : '1.25rem'
          }}>
            {/* Profile Image */}
            <div style={{ position: 'relative' }}>
              <div style={{
                width: isMobile ? '60px' : '80px',
                height: isMobile ? '60px' : '80px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isMobile ? '1.5rem' : '2rem',
                fontWeight: 'bold',
                border: '3px solid rgba(255, 255, 255, 0.3)',
                flexShrink: 0,
                overflow: 'hidden',
                backgroundImage: getProfileImageUrl() ? `url(${getProfileImageUrl()})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}>
                {!getProfileImageUrl() && getUserInitials(profileData.name)}
              </div>

              {/* Image Upload Button */}
              {isEditing && (
                <div style={{
                  position: 'absolute',
                  bottom: '2px',
                  right: '2px',
                  display: 'flex',
                  gap: '0.3rem'
                }}>
                  <label style={{
                    background: 'rgba(255, 255, 255, 0.92)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '26px',
                    height: '26px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                    transition: 'all 0.2s ease'
                  }}>
                    📷
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                  </label>

                  {(imagePreview || profileData.profileImage) && (
                    <button
                      onClick={imagePreview ? handleImageRemove : handleDeleteProfileImage}
                      style={{
                        background: 'rgba(255, 0, 0, 0.8)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '26px',
                        height: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: '0.7rem',
                        color: 'white',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              )}
            </div>

            <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
              <h1 style={{
                margin: '0 0 0.15rem 0',
                fontSize: isMobile ? '1.05rem' : '1.4rem',
                fontWeight: '700',
                letterSpacing: '-0.01em'
              }}>
                {profileData.name || sidebarData.name || 'Teacher Name'}
              </h1>
              <p style={{
                margin: '0 0 0.15rem 0',
                fontSize: isMobile ? '0.72rem' : '0.85rem',
                opacity: 0.9
              }}>
                🏷️ ID: {profileData.teacherId || sidebarData.teacherId || 'Teacher ID'}
              </p>
              <p style={{
                margin: 0,
                fontSize: isMobile ? '0.68rem' : '0.8rem',
                opacity: 0.8
              }}>
                💼 {profileData.designation || sidebarData.designation || 'Designation'} • 🏛️ {profileData.department || sidebarData.department || 'Department'}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '0.6rem',
            flexShrink: 0
          }}>
            {!isEditing ? (
              <button
                onClick={handleEdit}
                style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  border: '1.5px solid rgba(255, 255, 255, 0.3)',
                  color: '#ffffff',
                  padding: isMobile ? '0.4rem 0.9rem' : '0.5rem 1.1rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: isMobile ? '0.75rem' : '0.82rem',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem'
                }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.25)'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'; }}
              >
                ✏️ Edit Profile
              </button>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  style={{
                    background: '#ffffff',
                    border: 'none',
                    color: '#238548',
                    padding: isMobile ? '0.4rem 0.9rem' : '0.5rem 1.1rem',
                    borderRadius: '8px',
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    fontSize: isMobile ? '0.75rem' : '0.82rem',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    opacity: isSaving ? 0.7 : 1
                  }}
                >
                  {isSaving ? '⏳ Saving...' : '✅ Save'}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  style={{
                    background: 'rgba(255, 255, 255, 0.15)',
                    border: '1.5px solid rgba(255, 255, 255, 0.3)',
                    color: '#ffffff',
                    padding: isMobile ? '0.4rem 0.9rem' : '0.5rem 1.1rem',
                    borderRadius: '8px',
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    fontSize: isMobile ? '0.75rem' : '0.82rem',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    opacity: isSaving ? 0.7 : 1
                  }}
                >
                  ❌ Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {(saveMessage || saveError) && (
        <div style={{
          background: saveMessage ? '#edf7ed' : '#fdecea',
          border: `1px solid ${saveMessage ? '#c8e6c9' : '#f5c6cb'}`,
          color: saveMessage ? '#2e7d32' : '#c62828',
          padding: '0.6rem 0.9rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.4rem',
          fontSize: '0.82rem'
        }}>
          <span style={{ fontSize: '1rem' }}>{saveMessage ? '✅' : '❌'}</span>
          {saveMessage || saveError}
        </div>
      )}

      {/* Profile Information Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(360px, 1fr))',
        gap: isMobile ? '0.75rem' : '1.25rem'
      }}>
        {/* Personal Information Card */}
        <div style={{
          background: '#ffffff',
          borderRadius: '10px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
          overflow: 'hidden',
          border: '1px solid #eef0f2'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #238548, #1a6b38)',
            color: '#ffffff',
            padding: isMobile ? '0.6rem 0.75rem' : '0.75rem 1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span style={{ fontSize: isMobile ? '1rem' : '1.15rem' }}>👤</span>
            <h3 style={{ margin: 0, fontSize: isMobile ? '0.82rem' : '0.92rem', fontWeight: '600' }}>Personal Information</h3>
          </div>
          <div>
            <ProfileField label="Full Name" value={profileData.name} field="name" />
            <ProfileField label="Email" value={profileData.email} field="email" type="email" />
            <ProfileField label="Phone" value={profileData.phone} field="phone" type="tel" />
            <ProfileField label="Blood Group" value={profileData.bloodGroup} field="bloodGroup" />
            <ProfileField label="PAN Number" value={profileData.panNumber} field="panNumber" />
            <ProfileField label="Aadhaar No." value={profileData.adhaarNumber} field="adhaarNumber" />
          </div>
        </div>

        {/* Professional Information Card */}
        <div style={{
          background: '#ffffff',
          borderRadius: '10px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
          overflow: 'hidden',
          border: '1px solid #eef0f2'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #238548, #1a6b38)',
            color: '#ffffff',
            padding: isMobile ? '0.6rem 0.75rem' : '0.75rem 1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: isMobile ? '1rem' : '1.15rem' }}>🎓</span>
              <h3 style={{ margin: 0, fontSize: isMobile ? '0.82rem' : '0.92rem', fontWeight: '600' }}>Professional Information</h3>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.3rem',
              backgroundColor: 'rgba(255, 255, 255, 0.18)',
              padding: '0.2rem 0.5rem',
              borderRadius: '12px',
              fontSize: '0.62rem',
              fontWeight: '600'
            }}>
              🔒 Protected
            </div>
          </div>
          <div>
            <ProfileField label="Teacher ID" value={profileData.teacherId} field="teacherId" readonly />
            <ProfileField label="Username" value={profileData.username} field="username" readonly />
            <ProfileField label="Department" value={profileData.department} field="department" readonly />
            <ProfileField label="Designation" value={profileData.designation} field="designation" readonly />
            <ProfileField label="Qualification" value={profileData.academicQualification} field="academicQualification" type="textarea" readonly />
            <ProfileField label="Batch Name" value={profileData.batchName} field="batchName" readonly />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TeacherProfile;